import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Download, RefreshCw, IndianRupee, CheckCircle2, Clock, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg, fmtDate } from "@/lib/api";

const statusStyle = (s) => {
  const k = (s || "").toLowerCase();
  if (k === "paid") return "bg-green-100 text-green-800 hover:bg-green-100";
  if (k === "pending" || k === "active") return "bg-amber-100 text-amber-800 hover:bg-amber-100";
  if (k === "failed" || k === "expired" || k === "cancelled") return "bg-red-100 text-red-800 hover:bg-red-100";
  return "bg-slate-100 text-slate-700 hover:bg-slate-100";
};

const csvEscape = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function AdminPayments() {
  const [data, setData] = useState({ orders: [], stats: { total_orders: 0, paid_count: 0, pending_count: 0, total_collected: 0 } });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [reverifying, setReverifying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/payments");
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reverify = async (orderId) => {
    setReverifying(orderId);
    try {
      const r = await api.post(`/admin/payments/${orderId}/reverify`);
      toast.success(`Order is ${r.data.status || r.data.order_status || "updated"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setReverifying(null); }
  };

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return data.orders.filter((o) => {
      if (status !== "all" && (o.status || "").toLowerCase() !== status) return false;
      if (!needle) return true;
      return `${o.student_name || ""} ${o.student_email || ""} ${o.order_id || ""}`.toLowerCase().includes(needle);
    });
  }, [data.orders, q, status]);

  const exportCsv = () => {
    const headers = ["Order ID", "Student Name", "Student Email", "Amount", "Currency", "Status", "Cashfree Status", "Env", "Created At", "Updated At"];
    const lines = [headers.join(",")];
    shown.forEach((o) => {
      lines.push([o.order_id, o.student_name, o.student_email, o.amount, o.currency, o.status, o.cf_status, o.env, o.created_at, o.updated_at].map(csvEscape).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `pinslib-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const stats = data.stats || {};

  return (
    <div data-testid="admin-payments-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="small-label">Control Panel</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Payments</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">Every ₹69 signup activation order with its Cashfree status. Use Re-verify to pull the latest status for pending orders.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="admin-payments-refresh"><RefreshCw className="h-4 w-4 mr-1.5" />Refresh</Button>
          <Button onClick={exportCsv} disabled={!shown.length} data-testid="admin-payments-export" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white"><Download className="h-4 w-4 mr-1.5" />Export CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="admin-payments-stat-collected">
          <div className="small-label flex items-center gap-1.5"><IndianRupee className="h-3.5 w-3.5" />Collected</div>
          <div className="font-heading text-2xl font-bold mt-1">₹{(stats.total_collected || 0).toLocaleString("en-IN")}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="admin-payments-stat-paid">
          <div className="small-label flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-green-600" />Paid</div>
          <div className="font-heading text-2xl font-bold mt-1">{stats.paid_count || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="admin-payments-stat-pending">
          <div className="small-label flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-amber-600" />Pending</div>
          <div className="font-heading text-2xl font-bold mt-1">{stats.pending_count || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="admin-payments-stat-total">
          <div className="small-label flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5" />Total orders</div>
          <div className="font-heading text-2xl font-bold mt-1">{stats.total_orders || 0}</div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="relative max-w-sm w-full">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email, name or order id" className="pl-9 bg-white" data-testid="admin-payments-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 bg-white" data-testid="admin-payments-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <Table data-testid="admin-payments-table">
          <TableHeader>
            <TableRow>
              <TableHead>Student</TableHead>
              <TableHead>Order ID</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Env</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Loading payments…</TableCell></TableRow>}
            {!loading && shown.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">No payment orders found.</TableCell></TableRow>}
            {!loading && shown.map((o) => (
              <TableRow key={o.order_id} data-testid={`admin-payment-row-${o.order_id}`}>
                <TableCell>
                  <div className="font-medium">{o.student_name || "—"}</div>
                  <div className="text-xs text-slate-500">{o.student_email || "Account removed"}</div>
                </TableCell>
                <TableCell className="font-mono text-xs">{o.order_id}</TableCell>
                <TableCell className="font-mono">₹{o.amount}</TableCell>
                <TableCell>
                  <Badge className={statusStyle(o.status)} data-testid={`admin-payment-status-${o.order_id}`}>
                    {(o.status || "pending").toUpperCase()}
                  </Badge>
                  {o.cf_status && o.cf_status.toLowerCase() !== (o.status || "").toLowerCase() && (
                    <div className="text-[10px] text-slate-400 mt-1 font-mono">CF: {o.cf_status}</div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="font-mono text-[10px] uppercase">{o.env || "—"}</Badge>
                </TableCell>
                <TableCell className="text-slate-500 text-sm">{fmtDate(o.created_at)}</TableCell>
                <TableCell className="text-slate-500 text-sm">{o.updated_at ? fmtDate(o.updated_at) : "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => reverify(o.order_id)} disabled={reverifying === o.order_id} data-testid={`admin-payment-reverify-${o.order_id}`}>
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${reverifying === o.order_id ? "animate-spin" : ""}`} />
                    Re-verify
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
